import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiCalendarClock } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { EmptyState } from '../ui/empty-state'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { cn } from '../../lib/utils'
import type { Intervention } from '../../hooks/useInterventions'
import { useAssetInspections } from '../../hooks/useComplianceApi'
import { buildCalendar, type CalendarCategory, type CalendarFilter, type CalendarMarker, type CalendarState } from './calendar-model'

/**
 * The interventions calendar (spec 2026-10-25-22): twelve months back, twelve
 * ahead, one row per equipment. It replaces the preventive strip, which only
 * looked ahead and only at generated occurrences.
 *
 * The marker's shape says what it is — a round maintenance visit, a diamond
 * regulatory check, a square for anything else — and its colour the state. An
 * equipment subject to a periodic check that has no date in the window shows a
 * dashed « ! » in the current month: the gap a manager has to close.
 *
 * It reads the same interventions as the list one click away; an occurrence is
 * an intervention, never a separate object.
 */

const STATE_FILL: Record<CalendarState, string> = {
    planned: 'bg-muted-foreground/40 border border-muted-foreground/50',
    ordered: 'bg-info',
    done: 'bg-success',
    overdue: 'bg-destructive',
}

const SHAPE: Record<CalendarCategory, string> = {
    maintenance: 'rounded-full',
    check: 'rotate-45 rounded-[1px]',
    other: 'rounded-[2px]',
}

export function MarkerGlyph({ category, state, className }: { category: CalendarCategory; state: CalendarState; className?: string }) {
    return <span aria-hidden className={cn('inline-block h-2.5 w-2.5', SHAPE[category], STATE_FILL[state], className)} />
}

export function InterventionCalendar({ org, assetUid, interventions, isLoading, filter, onFilterChange, onSelect }: {
    org: string
    assetUid: string
    interventions: Intervention[]
    isLoading: boolean
    filter: CalendarFilter
    onFilterChange: (filter: CalendarFilter) => void
    onSelect?: (interventionUid: string) => void
}) {
    const { t, i18n } = useTranslation('interventions')
    const lang = i18n.language?.split('-')[0] || 'en'
    const { data: inspections = [] } = useAssetInspections(org, assetUid, true)
    const now = useMemo(() => new Date(), [])
    const { months, rows } = useMemo(
        () => buildCalendar(interventions, inspections, now, lang, filter),
        [interventions, inspections, now, lang, filter],
    )
    const counts = useMemo(() => {
        const markers = rows.flatMap((r) => r.markers)
        return {
            overdue: markers.filter((m) => m.state === 'overdue').length,
            gaps: rows.filter((r) => r.undatedChecks.length > 0).length,
        }
    }, [rows])

    const monthLabel = (year: number, month: number) =>
        new Date(year, month, 1).toLocaleDateString(i18n.language, { month: 'short' })
    const tooltip = (m: CalendarMarker) => t('calendar.marker_tooltip', {
        category: t(`calendar.category_${m.category}`),
        title: m.title,
        date: m.date.toLocaleDateString(i18n.language),
        state: t(`schedule.legend_${m.state}`),
    })

    if (isLoading) {
        return (
            <div className="rounded-lg border border-border bg-card p-4" data-testid="maintenance-schedule-loading">
                <div className="h-4 w-48 animate-pulse rounded bg-muted" />
                <div className="mt-4 h-24 w-full animate-pulse rounded bg-muted" />
            </div>
        )
    }

    return (
        <div className="rounded-lg border border-border bg-card p-4" data-testid="maintenance-schedule">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <AppIcon tone="muted" path={mdiCalendarClock} size="md" />
                        <h3 className="text-sm font-medium">{t('calendar.title')}</h3>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t('calendar.subtitle')}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {counts.gaps > 0 && (
                        <Badge variant="outline" className="border-warning/40 font-normal text-warning" data-testid="calendar-count-gaps">
                            {t('calendar.count_gaps', { count: counts.gaps })}
                        </Badge>
                    )}
                    {counts.overdue > 0 && (
                        <Badge variant="destructive" className="font-normal" data-testid="schedule-count-overdue">
                            {t('schedule.count_overdue', { count: counts.overdue })}
                        </Badge>
                    )}
                    <Select value={filter} onValueChange={(v) => onFilterChange(v as CalendarFilter)}>
                        <SelectTrigger className="h-8 w-[220px]" data-testid="calendar-category-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('calendar.filter_all')}</SelectItem>
                            <SelectItem value="maintenance">{t('calendar.category_maintenance')}</SelectItem>
                            <SelectItem value="check">{t('calendar.category_check')}</SelectItem>
                            <SelectItem value="other">{t('calendar.category_other')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {rows.length === 0 ? (
                <EmptyState variant="inline" live className="mt-4" title={t('calendar.empty')} data-testid="maintenance-schedule-empty" />
            ) : (
                <div className="mt-4 overflow-x-auto">
                    <div className="min-w-[960px]">
                        <div className="flex items-end gap-1 border-b border-border pb-1 text-[10px] text-muted-foreground">
                            <div className="w-52 shrink-0" />
                            {months.map((m) => (
                                <div
                                    key={`${m.year}-${m.month}`}
                                    className={cn('flex-1 text-center', m.offset === 0 && 'font-semibold text-foreground')}
                                >
                                    {m.month === 0 || m.offset === -12 ? <div className="text-[9px]">{m.year}</div> : null}
                                    {monthLabel(m.year, m.month)}
                                </div>
                            ))}
                        </div>
                        {rows.map((row) => (
                            <div key={row.assetUid} className="flex items-center gap-1 border-b border-border/50 py-1.5 last:border-0" data-testid="schedule-row">
                                <div className="w-52 shrink-0 pr-2">
                                    <Link
                                        to="/$org/assets/$assetUid"
                                        params={{ org, assetUid: row.assetUid }}
                                        className="block truncate text-xs font-medium hover:underline"
                                        title={row.assetName}
                                    >
                                        {row.assetName || '—'}
                                    </Link>
                                </div>
                                {months.map((m) => {
                                    const cell = row.markers.filter((o) => o.date.getFullYear() === m.year && o.date.getMonth() === m.month)
                                    const gap = m.offset === 0 && row.undatedChecks.length > 0
                                    return (
                                        <div
                                            key={`${m.year}-${m.month}`}
                                            className={cn('flex min-h-5 flex-1 items-center justify-center gap-1', m.offset === 0 && 'rounded bg-primary/5', m.offset < 0 && 'bg-muted/20')}
                                        >
                                            {cell.map((marker) => (
                                                <button
                                                    key={marker.uid}
                                                    type="button"
                                                    disabled={!marker.interventionUid}
                                                    onClick={() => marker.interventionUid && onSelect?.(marker.interventionUid)}
                                                    aria-label={tooltip(marker)}
                                                    title={tooltip(marker)}
                                                    data-testid={`schedule-occurrence-${marker.state}`}
                                                    data-category={marker.category}
                                                    className="inline-flex h-4 w-4 items-center justify-center transition-transform hover:scale-125 disabled:cursor-default"
                                                >
                                                    <MarkerGlyph category={marker.category} state={marker.state} />
                                                </button>
                                            ))}
                                            {gap && (
                                                <span
                                                    className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-dashed border-warning text-[9px] font-bold text-warning"
                                                    title={t('calendar.gap_tooltip', { checks: row.undatedChecks.join(', ') })}
                                                    aria-label={t('calendar.gap_tooltip', { checks: row.undatedChecks.join(', ') })}
                                                    data-testid="calendar-gap"
                                                >
                                                    !
                                                </span>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground" data-testid="calendar-legend">
                {(['maintenance', 'check', 'other'] as CalendarCategory[]).map((category) => (
                    <span key={category} className="flex items-center gap-1">
                        <MarkerGlyph category={category} state="planned" />
                        {t(`calendar.category_${category}`)}
                    </span>
                ))}
                <span className="mx-1 text-border">|</span>
                {(['planned', 'ordered', 'done', 'overdue'] as CalendarState[]).map((state) => (
                    <span key={state} className="flex items-center gap-1">
                        <span className={cn('h-2 w-2 rounded-full', STATE_FILL[state])} />
                        {t(`schedule.legend_${state}`)}
                    </span>
                ))}
                <span className="flex items-center gap-1">
                    <span className="inline-flex h-3 w-3 items-center justify-center rounded-full border border-dashed border-warning text-[8px] font-bold text-warning">!</span>
                    {t('calendar.legend_gap')}
                </span>
            </div>
        </div>
    )
}
